import React, { useEffect, useMemo, useState } from "react";
import { Search, Layers } from "lucide-react";
import api, { formatErr } from "../lib/api";

/** Symbol Library panel:
 *  - Shown as a right-hand drawer inside the workspace (not full-screen).
 *  - Can be used as a reference (see properties) and as a palette for the editor (onPick).
 */
export default function SymbolLibrary({ onPick, onClose, embedded = false }) {
  const [data, setData] = useState({ categories: [], symbols: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/symbols");
        setData(data);
        setSel(data.symbols[0] || null);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return data.symbols.filter((s) => {
      if (cat !== "All" && s.category !== cat) return false;
      if (!q) return true;
      const h = (s.name + " " + s.tag_prefix + " " + (s.standards || []).join(" ") + " " + s.id).toLowerCase();
      return h.includes(q.toLowerCase());
    });
  }, [data.symbols, cat, q]);

  // Cap the rendered grid so a 1000+ library stays snappy. Users refine
  // by the search input or category chip to narrow further.
  const RENDER_CAP = 240;
  const visible = filtered.slice(0, RENDER_CAP);
  const hasMore = filtered.length > RENDER_CAP;

  const container = (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", height: "100%", minHeight: 480 }}>
      {/* LEFT - list */}
      <div style={{ borderRight: "1px solid var(--pf-border)", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ padding: 10, display: "flex", gap: 8, alignItems: "center", borderBottom: "1px solid var(--pf-border)" }}>
          <Search size={14} style={{ color: "var(--pf-text-muted)" }}/>
          <input className="pf-input" style={{ padding: "6px 8px", fontSize: 12 }} placeholder={`Search ${data.symbols.length.toLocaleString()} symbols — name, tag, standard…`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="lib-search"/>
          <span className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)", whiteSpace: "nowrap" }} data-testid="lib-result-count">
            {filtered.length.toLocaleString()} / {data.symbols.length.toLocaleString()}
          </span>
        </div>
        <div style={{ display: "flex", gap: 4, padding: "8px 10px", flexWrap: "wrap", borderBottom: "1px solid var(--pf-border)", background: "var(--pf-surface-2)" }}>
          {["All", ...data.categories].map((c) => (
            <button key={c} onClick={() => setCat(c)} className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 11, borderColor: cat === c ? "var(--pf-accent)" : "var(--pf-border)", color: cat === c ? "var(--pf-accent)" : "var(--pf-text-muted)" }} data-testid={`lib-cat-${c}`}>
              {c}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 10 }}>
          {err && <div className="pf-sev pf-sev-error" style={{ display: "block", padding: 8 }}>{err}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
            {visible.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setSel(s);
                  // In picker mode (onPick provided), a single click also
                  // arms the symbol for click-to-place on the canvas.
                  // Without onPick (reference-only mode) we just preview.
                  if (onPick) onPick(s);
                }}
                onDoubleClick={() => onPick?.(s)}
                style={{ padding: 8, background: sel?.id === s.id ? "rgba(255,176,0,0.08)" : "var(--pf-surface-2)", border: sel?.id === s.id ? "1px solid var(--pf-accent)" : "1px solid var(--pf-border)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, color: "var(--pf-text)" }}
                title={onPick ? "Click to arm for placement (then click canvas to drop)" : "Click to preview"}
                data-testid={`lib-symbol-${s.id}`}
              >
                <svg viewBox="0 0 80 80" width="64" height="64" style={{ background: "var(--pf-blueprint)" }} dangerouslySetInnerHTML={{ __html: s.svg }}/>
                <div className="pf-mono" style={{ fontSize: 10, lineHeight: 1.2, textAlign: "center" }}>{s.name}</div>
              </button>
            ))}
          </div>
          {hasMore && (
            <div className="pf-mono" style={{ marginTop: 12, padding: 10, textAlign: "center", fontSize: 11, color: "var(--pf-text-dim)", border: "1px dashed var(--pf-border)" }} data-testid="lib-more-hint">
              Showing first {RENDER_CAP} of {filtered.length.toLocaleString()}. Refine search or pick a category chip to narrow.
            </div>
          )}
          {filtered.length === 0 && (
            <div className="pf-mono" style={{ padding: 24, textAlign: "center", color: "var(--pf-text-dim)" }} data-testid="lib-empty">No symbols match "{q}" in {cat}.</div>
          )}
        </div>
      </div>

      {/* RIGHT - details */}
      <div style={{ padding: 16, overflowY: "auto" }}>
        {sel ? (
          <>
            <div className="pf-overline" style={{ marginBottom: 6 }}>{sel.category}</div>
            <div className="pf-heading" style={{ fontSize: 18, marginBottom: 8 }}>{sel.name}</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              <span className="pf-ind-chip">TAG PREFIX: {sel.tag_prefix}</span>
              {(sel.standards || []).map((st) => <span key={st} className="pf-ind-chip">{st}</span>)}
            </div>
            <svg viewBox="0 0 80 80" width="180" height="180" style={{ background: "var(--pf-blueprint)", border: "1px solid var(--pf-border)" }} dangerouslySetInnerHTML={{ __html: sel.svg }}/>
            <div className="pf-overline" style={{ marginTop: 18, marginBottom: 8 }}>PROPERTIES (DATASHEET FIELDS)</div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {sel.properties.map((p) => (
                <li key={p} className="pf-mono" style={{ padding: "6px 0", borderBottom: "1px solid var(--pf-border-muted)", fontSize: 12, color: "var(--pf-text)" }}>
                  {p}
                </li>
              ))}
            </ul>
            {onPick && (
              <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => onPick(sel)} data-testid="lib-insert">Insert into diagram</button>
            )}
          </>
        ) : (
          <div style={{ color: "var(--pf-text-muted)" }}>Select a symbol to view details.</div>
        )}
      </div>
    </div>
  );

  if (embedded) return container;

  // Full-panel mode (for standalone "Symbols" tab)
  return (
    <div className="pf-card" style={{ padding: 0, overflow: "hidden", height: "calc(100vh - 220px)", display: "flex", flexDirection: "column" }} data-testid="symbol-library">
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border)", display: "flex", alignItems: "center", gap: 10 }}>
        <Layers size={14} style={{ color: "var(--pf-accent)" }}/>
        <div className="pf-overline">SYMBOL LIBRARY · ISA 5.1 / ISO 10628 / TEMA · {data.symbols.length.toLocaleString()} SYMBOLS</div>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{container}</div>
    </div>
  );
}
